// Copyright 2026 innoLab Canada Inc. All rights reserved.
//
// New Project Interview chat bundle (brief .9 / Usewright .4).
//
// RENDER-SAFETY CONTRACT (Usewright RS-1..RS-4, Aegis AC-XSS-4) — the single most load-bearing
// rule in this file:
//   - Every agent-supplied or user-supplied string (question prompt, option Label/Description,
//     discussion body, author display name, completion summary) is injected into the DOM via
//     textContent / DOM-builder APIs ONLY.
//   - This file contains ZERO uses of innerHTML / outerHTML / insertAdjacentHTML / document.write
//     / jQuery .html(). The static grep gate (web_src/js/features/interview-chat.innerhtml.test.ts)
//     asserts zero hits and GATES this brief.
//   - An option Value is the answer body; it rides in dataset.value, is never displayed, and is
//     never assembled into a markup string (RS-2). It is read back via dataset.value at click time.
//   - SSE-arrived content uses the IDENTICAL textContent render path as first-load content (RS-4):
//     there is no second, looser path.

import {registerGlobalInitFunc} from '../modules/observer.ts';
import {POST} from '../modules/fetch.ts';

type QuestionOption = {
  label?: string;
  value?: string;
  description?: string;
};

// QuestionPayload mirrors services/interview.InterviewQuestionPayload: the structured question
// carried on a bot-authored new-comment event (and seeded on first load via interviewActive). Every
// text field (prompt, option label/description) is rendered via textContent only; value rides in a
// data-value attribute (RS-1/RS-2).
type QuestionPayload = {
  comment_id?: number;
  prompt?: string;
  options?: QuestionOption[];
  default_answer?: string;
  allow_free_text?: boolean;
};

type SSEPayload = {
  kind: string; // 'new-comment' | 'dispatch-failed' | 'completion'
  issue_id?: number;
  comment_id?: number;
  author_name?: string;
  author_is_bot?: boolean;
  is_answer?: boolean;
  is_discussion?: boolean;
  ref_comment_id?: number;
  // question carries the structured StratosQuestion prompt + Options when this comment IS an open
  // interview question (bot-authored). Absent for every non-question comment.
  question?: QuestionPayload;
  // repo_url is the VALIDATED in-org redirect path on a completion event. The server produced it
  // via ValidateInOrgRepoURL; it is always a site-root-relative path ('/org/repo'). The client
  // navigates to it as-is and never constructs a URL from user free-text (Aegis AC-LAT-1, IX-14).
  repo_url?: string;
};

// ActiveSession mirrors routers/web/repo/interview.go interviewActiveSession: the LIVE repo/issue
// ids the answer POST must carry (never RepoID: 0) plus the open question seeded on first load.
type ActiveSession = {
  repo_id?: number;
  issue_id?: number;
  question?: QuestionPayload;
};

// BubbleInfo is the minimal attribution a bubble/discussion renderer needs. Both an SSE payload
// and a locally-built optimistic bubble satisfy it, so the render path is identical (RS-4).
type BubbleInfo = {author_name?: string};

type I18n = Record<string, string>;

// ResumableInterview mirrors services/interview.ResumableInterview. Title is issue-owned text and
// is rendered via textContent only (RS-1); Link is a server-built, same-origin issue path.
type ResumableInterview = {
  issue_id?: number;
  title?: string;
  link?: string;
  last_activity?: number;
};

const MAX_OPTIONS = 20; // Aegis AC-XSS-1: server bounds option count; defence-in-depth here.

function i18n(): I18n {
  const data = (window as any).config?.pageData?.interviewI18n;
  return (data && typeof data === 'object') ? data as I18n : {};
}

// pageResumable / pageHistory read the server-provided resume list and prior thread from PageData.
// Both are delivered through Gitea's safe JSON PageData path (no markup interpolation); every text
// field within them is still rendered via textContent on the client (RS-1).
function pageResumable(): ResumableInterview[] {
  const data = (window as any).config?.pageData?.interviewResumable;
  return Array.isArray(data) ? data as ResumableInterview[] : [];
}

function pageHistory(): SSEPayload[] {
  const data = (window as any).config?.pageData?.interviewHistory;
  return Array.isArray(data) ? data as SSEPayload[] : [];
}

// pageActive reads the live-session descriptor (repo/issue ids + open question) the server seeds
// when the page addresses an interview the user participates in. Null when no interview is
// addressed (the empty/start-new entry state).
function pageActive(): ActiveSession | null {
  const data = (window as any).config?.pageData?.interviewActive;
  return (data && typeof data === 'object') ? data as ActiveSession : null;
}

// pageCsrf reads the per-session interview CSRF synchronizer-token the server seeds (same PageData
// path as interviewActive/interviewI18n). Every state-changing POST below echoes it in the
// X-Csrf-Token header so services/interview.ValidateInterviewCSRF can match it against the
// session-bound secret; a cross-site forgery cannot read this value and so cannot forge the header.
function pageCsrf(): string {
  const data = (window as any).config?.pageData?.interviewCsrf;
  return (typeof data === 'string') ? data : '';
}

// csrfHeaders builds the request headers carrying the interview synchronizer-token for a POST.
function csrfHeaders(): Record<string, string> {
  return {'X-Csrf-Token': pageCsrf()};
}

// el is a tiny DOM-builder: it sets textContent (never innerHTML) and attributes only. data-*
// attributes (e.g. the option Value, RS-2) are written via setAttribute('data-...'), the project
// convention, which is markup-free exactly like dataset.
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: {class?: string; text?: string; attrs?: Record<string, string>} = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text; // textContent ONLY (RS-1)
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  return node;
}

class InterviewChat {
  private root: HTMLElement;
  private transcript: HTMLElement;
  private working: HTMLElement;
  private failure: HTMLElement;
  private complete: HTMLElement;
  private active: HTMLElement;
  private composer: HTMLFormElement;
  private composerInput: HTMLTextAreaElement;
  private modePill: HTMLElement;
  private sendBtn: HTMLButtonElement;
  private answerUrl: string;
  private streamUrl: string;
  private workingTimeoutMs: number;
  private workingTimer: number | null = null;
  private source: EventSource | null = null;
  private seenComments = new Set<number>(); // dedupe by comment id (idempotent reconnect)
  private issueId = 0;
  private repoId = 0; // the LIVE intake repo id, seeded from interviewActive (never 0 on answer)
  private activeQuestionId = 0; // comment id of the rendered open question (dedupe re-render)
  private lastFailureKind: 'post' | 'dispatch' | null = null;
  private redirectPath = ''; // validated in-org path from the completion event (RS / IX-14)
  private redirectTimer: number | null = null;
  private t: I18n;

  constructor(root: HTMLElement) {
    this.root = root;
    this.t = i18n();
    this.transcript = this.q('transcript');
    this.working = this.q('working');
    this.failure = this.q('failure');
    this.complete = this.q('complete');
    this.active = this.q('active');
    this.composer = this.q('composer') as HTMLFormElement;
    this.composerInput = this.q('composer-input') as HTMLTextAreaElement;
    this.modePill = this.q('mode-pill');
    this.sendBtn = this.q('send') as HTMLButtonElement;
    this.answerUrl = root.getAttribute('data-answer-url') || '';
    this.streamUrl = root.getAttribute('data-stream-url') || '';
    this.workingTimeoutMs = Number(root.getAttribute('data-working-timeout-ms')) || 30000;
  }

  private q(role: string): HTMLElement {
    const node = this.root.querySelector<HTMLElement>(`[data-role="${role}"]`);
    if (!node) throw new Error(`interview-chat: missing region [data-role="${role}"]`);
    return node;
  }

  init() {
    this.bindComposer();
    this.bindFailureActions();
    this.hydrateActive(); // seed live ids + open question BEFORE history/stream use them
    this.hydrateResume();
    this.hydrateHistory();
    this.maybeShowStart();
    this.openStream();
  }

  // hydrateActive seeds the LIVE repo/issue ids and the open question from the server-provided
  // interviewActive descriptor. Seeding repoId/issueId here is what makes the answer POST carry the
  // REAL ids (never RepoID: 0); seeding the question renders the active panel on first load, not
  // only when the next SSE event arrives.
  private hydrateActive() {
    const active = pageActive();
    if (!active) return;
    if (active.repo_id) this.repoId = active.repo_id;
    if (active.issue_id) this.issueId = active.issue_id;
    if (active.question) this.renderQuestion(active.question);
  }

  // maybeShowStart reveals the "Start a new interview" affordance ONLY in the empty entry state:
  // no interview addressed (no live issue), no question rendered and no resumable to continue. When
  // an interview is addressed the chat shows that conversation instead.
  private maybeShowStart() {
    if (this.issueId !== 0 || pageResumable().length > 0) return;
    const start = this.root.querySelector<HTMLElement>('[data-role="start"]');
    if (!start) return;
    start.hidden = false;
    start.classList.remove('tw-hidden');
  }

  // ---- resume (brief .11) -------------------------------------------------------------------

  // hydrateResume renders the participant-scoped list of the user's in-progress interviews
  // (server-resolved; the client only DISPLAYS what the server scoped). Each title is issue-owned
  // text rendered via textContent (RS-1); each link is a server-built same-origin issue path.
  private hydrateResume() {
    const items = pageResumable();
    const region = this.root.querySelector<HTMLElement>('[data-role="resume"]');
    const list = this.root.querySelector<HTMLElement>('[data-role="resume-list"]');
    if (!region || !list || items.length === 0) return;
    list.replaceChildren();
    for (const it of items) {
      const li = el('li', {class: 'interview-resume-item'});
      const link = el('a', {class: 'interview-resume-link', text: it.title || `#${it.issue_id ?? ''}`});
      // The link target is a server-supplied same-origin path; accept it only when it is one.
      const href = this.safeResumeHref(it.link || '');
      if (href) link.setAttribute('href', href);
      li.append(link);
      list.append(li);
    }
    region.hidden = false;
    region.classList.remove('tw-hidden');
  }

  // safeResumeHref accepts only a same-origin, single-leading-slash relative path, mirroring the
  // completion redirect guard, so a malformed server value never becomes an off-origin link.
  private safeResumeHref(raw: string): string {
    const p = raw.trim();
    if (!p.startsWith('/') || p.startsWith('//')) return '';
    if (p.includes('?') || p.includes('#') || p.includes('..') || p.includes('\\')) return '';
    return p;
  }

  // hydrateHistory replays the server-rendered prior thread through the IDENTICAL textContent
  // render path as live SSE (RS-4 / IX-1): there is no second, looser path for history. Each
  // payload is routed through the same onComment classifier, and dedupe by comment id means a
  // later live SSE re-send of the same comment does not double-render.
  private hydrateHistory() {
    const history = pageHistory();
    if (history.length === 0) return;
    for (const p of history) {
      if (p.issue_id) this.issueId = p.issue_id;
      this.onComment(p);
    }
  }

  // ---- SSE consumer -------------------------------------------------------------------------

  private openStream() {
    if (!this.streamUrl || !window.EventSource) return;
    this.source = new EventSource(this.streamUrl, {withCredentials: true});
    // Named 'interview' events only; ignore pings/control frames here.
    this.source.addEventListener('interview', (ev: MessageEvent) => this.onSSE(ev));
    this.source.addEventListener('error', () => this.announce(this.t.reconnecting || ''));
  }

  private onSSE(ev: MessageEvent) {
    let payload: SSEPayload;
    try {
      payload = JSON.parse(ev.data);
    } catch {
      return; // malformed payload: ignore rather than throw on the live channel
    }
    if (payload.issue_id) this.issueId = payload.issue_id;
    switch (payload.kind) {
      case 'new-comment': this.onComment(payload); break;
      case 'dispatch-failed': this.onDispatchFailed(); break;
      case 'completion': this.onCompletion(payload); break;
      default: break;
    }
  }

  private onComment(p: SSEPayload) {
    if (p.comment_id && this.seenComments.has(p.comment_id)) return; // dedupe (RS resilience)
    if (p.comment_id) this.seenComments.add(p.comment_id);
    this.hideEmpty();
    // An agent (bot) comment resolves the working state and advances the interview.
    if (p.author_is_bot) {
      this.clearWorking();
      this.appendAgentBubble(p);
      return;
    }
    if (p.is_discussion) {
      this.appendDiscussion(p);
      return;
    }
    // Another human's answer arriving while we wait: render as their answer bubble.
    this.appendHumanAnswer(p);
  }

  private onDispatchFailed() {
    // Server observed OverwatchDeliveryStatus == failed (design.md §9/§10, IX-10).
    this.showFailure('dispatch');
  }

  private onCompletion(p: SSEPayload) {
    // The completion marker is trusted by SSE identity (the server only emits it for the
    // Planwright bot uid, Aegis AC-LAT-1); the client does not scan any body for a marker string.
    // The redirect target is the server-VALIDATED in-org path (ValidateInOrgRepoURL); the client
    // accepts it only when it is a same-origin relative path ('/org/repo'), never a constructed or
    // absolute URL (Aegis AC-LAT-1 / Usewright IX-14: no open-redirect from the client either).
    this.redirectPath = this.safeRedirectPath(p.repo_url || '');
    this.clearWorking();
    this.renderCompletion();
  }

  // safeRedirectPath accepts ONLY a single-slash-leading, same-origin relative path of the shape
  // '/seg/seg'. A protocol-relative ('//host'), absolute ('http://'), back-tracking ('/a/../b') or
  // query/fragment-bearing value is rejected to the empty string, so the completion card falls back
  // to no auto-navigation rather than honour an off-origin target (defence-in-depth over the
  // server-side ValidateInOrgRepoURL).
  private safeRedirectPath(raw: string): string {
    const p = raw.trim();
    if (!p.startsWith('/') || p.startsWith('//')) return '';
    if (p.includes('?') || p.includes('#') || p.includes('..') || p.includes('\\')) return '';
    if (p.split('/').filter(Boolean).length !== 2) return '';
    return p;
  }

  // ---- bubble rendering (textContent-only) --------------------------------------------------

  private appendAgentBubble(p: SSEPayload) {
    const article = el('div', {class: 'interview-bubble agent', attrs: {role: 'article'}});
    article.append(el('div', {class: 'interview-author', text: p.author_name || 'Planwright'}));
    // When the comment IS a structured question, its prompt is the bubble body and the choice
    // chips / "You decide" / "Other…" render into the active-question panel. The prompt and every
    // option Label/Description are set via textContent only (RS-1); the option Value rides in a
    // data-value attribute (RS-2). A non-question agent statement renders just the attribution
    // bubble (its body, when present, would likewise be textContent).
    if (p.question?.prompt) {
      article.append(el('div', {class: 'interview-bubble-body', text: p.question.prompt}));
    }
    this.transcript.append(article);
    this.scrollIfPinned();
    if (p.question) this.renderQuestion(p.question);
    this.announce(p.author_name || 'Planwright');
  }

  // renderQuestion maps the wire QuestionPayload to the active-question panel renderer. It dedupes
  // by the question's comment id so a first-load seed and a later SSE re-send do not double-render.
  // Every field is forwarded to renderActiveQuestion, which is the single textContent/data-value
  // render path (RS-1/RS-2): there is no second, looser path for questions.
  private renderQuestion(q: QuestionPayload) {
    if (q.comment_id) {
      if (this.activeQuestionId === q.comment_id) return; // already the rendered active question
      this.activeQuestionId = q.comment_id;
      this.seenComments.add(q.comment_id);
    }
    this.renderActiveQuestion({
      prompt: q.prompt ?? '',
      options: q.options,
      defaultAnswer: q.default_answer,
      allowFreeText: q.allow_free_text,
    });
  }

  private appendHumanAnswer(p: BubbleInfo, label?: string, optimistic = false) {
    const wrap = el('div', {class: `interview-bubble answer${optimistic ? ' optimistic' : ''}`, attrs: {role: 'article'}});
    if (p.author_name) wrap.append(el('div', {class: 'interview-author', text: p.author_name}));
    wrap.append(el('div', {class: 'interview-bubble-body', text: label ?? ''}));
    this.transcript.append(wrap);
    this.scrollIfPinned();
    return wrap;
  }

  private appendDiscussion(p: BubbleInfo, body?: string) {
    const line = el('div', {class: 'interview-discussion', attrs: {role: 'note'}});
    line.append(el('span', {class: 'interview-discussion-glyph', attrs: {'aria-hidden': 'true'}, text: '\u{1F4AC}'}));
    if (p.author_name) line.append(el('span', {class: 'interview-discussion-author', text: p.author_name}));
    line.append(el('span', {class: 'interview-discussion-body', text: body ?? ''}));
    this.transcript.append(line);
    this.scrollIfPinned();
  }

  // ---- active-question panel (choice chips) -------------------------------------------------

  // renderActiveQuestion is the structured-question entry point used once the SSE question payload
  // is delivered (brief .10). It is exercised directly by the unit tests today. Every option field
  // is textContent (Label/Description) or a data-value attribute (Value); no field is markup.
  renderActiveQuestion(opts: {prompt: string; options?: QuestionOption[]; defaultAnswer?: string; allowFreeText?: boolean}) {
    this.active.replaceChildren();
    this.active.append(el('div', {class: 'interview-prompt', text: opts.prompt}));

    const chips = el('div', {class: 'interview-chips'});
    const options = (opts.options || []).slice(0, MAX_OPTIONS);
    if ((opts.options || []).length > MAX_OPTIONS) {
      console.warn('interview-chat: option count exceeds bound; rendering first 20');
    }
    chips.setAttribute('role', 'toolbar');
    chips.setAttribute('aria-label', this.t.modeAnswer || 'Answer');
    for (const [i, o] of options.entries()) {
      chips.append(this.buildChip(o, i, opts.defaultAnswer));
    }
    this.active.append(chips);
    this.wireChipKeyboardNav(chips);

    if (opts.defaultAnswer) this.active.append(this.buildYouDecide(opts.defaultAnswer, options));
    if (opts.allowFreeText) this.active.append(this.buildOtherButton());

    this.showActive();
  }

  private buildChip(o: QuestionOption, index: number, defaultAnswer?: string): HTMLElement {
    const wrap = el('div', {class: 'interview-chip-wrap'});
    const btn = el('button', {class: 'ui button interview-chip', attrs: {type: 'button'}});
    btn.append(el('span', {class: 'interview-chip-label', text: o.label ?? ''})); // Label via textContent
    btn.setAttribute('data-value', o.value ?? ''); // Value via data-value (RS-2); never displayed
    const isRecommended = defaultAnswer !== undefined && o.value === defaultAnswer;
    if (isRecommended) {
      btn.append(el('span', {class: 'interview-chip-star', attrs: {'aria-hidden': 'true'}, text: '★'}));
      btn.append(el('span', {class: 'tw-sr-only', text: ` ${this.t.recommended || 'recommended'}`}));
    }
    if (o.description) {
      const descId = `interview-desc-${index}`;
      const desc = el('div', {class: 'interview-chip-desc', text: o.description, attrs: {id: descId}});
      btn.setAttribute('aria-describedby', descId);
      wrap.append(btn);
      wrap.append(desc);
    } else {
      wrap.append(btn);
    }
    btn.addEventListener('click', () => this.submitAnswer(btn.getAttribute('data-value') || '', o.label ?? ''));
    return wrap;
  }

  // wireChipKeyboardNav makes the choice-chip row a roving-tabindex cluster (WCAG 2.1.1, Usewright
  // §9.2): exactly one chip is in the Tab order at a time, and ArrowLeft/Right (and Home/End) move
  // focus between chips. Enter/Space already activate a <button>, so this only adds intra-group
  // arrow traversal; every chip remains individually focusable and operable.
  private wireChipKeyboardNav(chips: HTMLElement) {
    const buttons = Array.from(chips.querySelectorAll<HTMLButtonElement>('button.interview-chip'));
    if (buttons.length === 0) return;
    const setRoving = (n: number) => {
      for (const [i, b] of buttons.entries()) b.setAttribute('tabindex', i === n ? '0' : '-1');
    };
    setRoving(0);
    const focusAt = (idx: number) => {
      const n = (idx + buttons.length) % buttons.length;
      setRoving(n);
      buttons[n].focus();
    };
    chips.addEventListener('keydown', (e: KeyboardEvent) => {
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (current < 0) return;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown': e.preventDefault(); focusAt(current + 1); break;
        case 'ArrowLeft': case 'ArrowUp': e.preventDefault(); focusAt(current - 1); break;
        case 'Home': e.preventDefault(); focusAt(0); break;
        case 'End': e.preventDefault(); focusAt(buttons.length - 1); break;
        default: break;
      }
    });
  }

  private buildYouDecide(defaultAnswer: string, options: QuestionOption[]): HTMLElement {
    const wrap = el('div', {class: 'interview-you-decide-wrap'});
    const btn = el('button', {class: 'ui basic button interview-you-decide', attrs: {type: 'button'}});
    btn.append(el('span', {text: this.t.youDecide || 'You decide'}));
    // Helper shows the matching option Label when available (friendlier), else the raw default.
    const match = options.find((o) => o.value === defaultAnswer);
    const shown = match?.label ?? defaultAnswer;
    wrap.append(btn);
    wrap.append(el('div', {class: 'interview-you-decide-help', text: `${this.t.youDecideRecommended || 'uses recommended'}: ${shown}`}));
    btn.addEventListener('click', () => this.submitAnswer(defaultAnswer, `${this.t.youDecide || 'You decide'}: ${shown}`));
    return wrap;
  }

  private buildOtherButton(): HTMLElement {
    const wrap = el('div', {class: 'interview-other-wrap'});
    const btn = el('button', {class: 'ui basic button interview-other', attrs: {type: 'button'}});
    btn.append(el('span', {text: this.t.other || 'Other…'}));
    btn.addEventListener('click', () => {
      this.composerInput.focus();
      this.updateMode();
    });
    wrap.append(btn);
    return wrap;
  }

  // ---- submit / optimistic send -------------------------------------------------------------

  private async submitAnswer(value: string, label: string) {
    if (!value) return;
    this.setPanelDisabled(true);
    const bubble = this.appendHumanAnswer({author_name: this.t.you || 'You'}, label, true);
    this.enterWorking();
    try {
      const resp = await POST(this.answerUrl, {data: {RepoID: this.repoId, IssueID: this.issueId, Content: value}, headers: csrfHeaders()});
      if (!resp.ok) {
        this.markOptimisticFailed(bubble);
        this.showFailure('post');
        return;
      }
      let body: any = {};
      try {
        body = await resp.json();
      } catch {
        body = {};
      }
      bubble.classList.remove('optimistic');
      if (body && body.is_answer === false) {
        // Concurrent loss: the question was already closed (IX-12).
        this.annotateAlreadyAnswered(bubble);
        this.clearWorking();
        this.setPanelDisabled(false);
      }
    } catch {
      this.markOptimisticFailed(bubble);
      this.showFailure('post');
    }
  }

  private markOptimisticFailed(bubble: HTMLElement) {
    bubble.classList.remove('optimistic');
    bubble.classList.add('not-sent');
  }

  private annotateAlreadyAnswered(bubble: HTMLElement, who = '') {
    const tmpl = this.t.alreadyAnswered || '%[1]s already answered this. Your choice was not applied.';
    const note = el('div', {class: 'interview-already-answered', attrs: {role: 'status'}, text: tmpl.replace('%[1]s', who)});
    bubble.append(note);
  }

  // ---- working / failure / completion states ------------------------------------------------

  private enterWorking() {
    this.failure.hidden = true;
    this.failure.classList.add('tw-hidden');
    this.working.hidden = false;
    this.working.classList.remove('tw-hidden');
    this.announce(this.t.working || 'Planwright is working');
    if (this.workingTimer) window.clearTimeout(this.workingTimer);
    // The timer is the real safety mechanism: the indicator must never spin forever (IX-10).
    this.workingTimer = window.setTimeout(() => this.showFailure(this.lastFailureKind ?? 'dispatch'), this.workingTimeoutMs);
  }

  private clearWorking() {
    if (this.workingTimer) {
      window.clearTimeout(this.workingTimer);
      this.workingTimer = null;
    }
    this.working.hidden = true;
    this.working.classList.add('tw-hidden');
    this.setPanelDisabled(false);
  }

  private showFailure(kind: 'post' | 'dispatch') {
    this.lastFailureKind = kind;
    if (this.workingTimer) {
      window.clearTimeout(this.workingTimer);
      this.workingTimer = null;
    }
    this.working.hidden = true;
    this.working.classList.add('tw-hidden');
    const body = this.failure.querySelector<HTMLElement>('[data-role="failure-body"]');
    if (body) body.textContent = kind === 'post' ? (this.t.failurePost || '') : (this.t.failureDispatch || '');
    this.failure.hidden = false;
    this.failure.classList.remove('tw-hidden');
  }

  private bindFailureActions() {
    this.failure.querySelector('[data-action="retry"]')?.addEventListener('click', () => {
      this.failure.hidden = true;
      this.failure.classList.add('tw-hidden');
      // Retry re-arms the working state; the answer is already recorded so no duplicate is posted
      // (Usewright OQ-2 default). A late SSE resolution reconciles normally.
      this.enterWorking();
    });
    this.failure.querySelector('[data-action="dismiss"]')?.addEventListener('click', () => {
      this.failure.hidden = true;
      this.failure.classList.add('tw-hidden');
      this.setPanelDisabled(false);
    });
  }

  private renderCompletion() {
    this.complete.replaceChildren();
    this.complete.append(el('div', {class: 'interview-complete-title', text: `✅ ${this.t.completeTitle || 'Your project is ready'}`}));
    const go = el('button', {class: 'ui primary button interview-complete-go', attrs: {type: 'button'}, text: this.t.completeGoNow || 'Go to project now'});
    // 'Go to project now' navigates immediately; the auto-redirect below gives a short, cancellable
    // delay first (Usewright IX-14 / OQ-5). Both honour only the same-origin validated path.
    if (this.redirectPath) {
      go.addEventListener('click', () => this.navigate());
      if (this.redirectTimer) window.clearTimeout(this.redirectTimer);
      this.redirectTimer = window.setTimeout(() => this.navigate(), 4000);
    } else {
      go.disabled = true; // no validated target: never auto-navigate, never enable a dead button
    }
    this.complete.append(go);
    this.complete.hidden = false;
    this.complete.classList.remove('tw-hidden');
    this.active.hidden = true;
    this.active.classList.add('tw-hidden');
    this.announce(this.t.completeTitle || '');
  }

  private navigate() {
    if (this.redirectTimer) {
      window.clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
    if (this.redirectPath) window.location.assign(this.redirectPath);
  }

  // ---- "you decide for the rest" affordance (D11) -------------------------------------------

  // maybeRenderDecideRest appends the consequential "You decide for the rest" affordance to the
  // active panel ONLY when the server reports the X-turn threshold has been reached (available ===
  // true). Before the threshold it is not rendered at all (Usewright IX-6: not merely disabled), so
  // it cannot be discovered prematurely. The server gate (DecideRestAvailable) is authoritative;
  // this is the UX surface of it. On confirm it posts the human-authored delegation marker body,
  // which Planwright recognises and which still graduates through the confirmation gate.
  renderDecideRest(available: boolean, delegationMarker: string) {
    const existing = this.active.querySelector('[data-role="decide-rest"]');
    if (existing) existing.remove();
    if (!available) return;
    const wrap = el('div', {class: 'interview-decide-rest', attrs: {'data-role': 'decide-rest'}});
    wrap.append(el('hr', {class: 'interview-decide-rest-divider'}));
    const trigger = el('button', {class: 'ui basic button interview-decide-rest-trigger', attrs: {type: 'button'}, text: this.t.youDecideRest || 'You decide for the rest'});
    trigger.addEventListener('click', () => {
      trigger.disabled = true;
      this.submitAnswer(delegationMarker, this.t.youDecideRest || 'You decide for the rest');
    });
    wrap.append(trigger);
    this.active.append(wrap);
  }

  // ---- composer (answer vs discussion) ------------------------------------------------------

  private bindComposer() {
    this.composerInput.addEventListener('input', () => this.updateMode());
    this.composerInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.composer.requestSubmit();
      }
    });
    this.composer.addEventListener('submit', (e) => {
      e.preventDefault();
      const content = this.composerInput.value.trim();
      if (!content) return;
      const isDiscussion = content.startsWith('@');
      if (isDiscussion) {
        this.postDiscussion(content);
      } else {
        this.submitAnswer(content, content);
      }
      this.composerInput.value = '';
      this.updateMode();
    });
    this.updateMode();
  }

  private updateMode() {
    const isDiscussion = this.composerInput.value.trimStart().startsWith('@');
    this.modePill.textContent = isDiscussion ? (this.t.modeDiscussion || 'Discussion') : (this.t.modeAnswer || 'Answer');
    this.modePill.classList.toggle('discussion', isDiscussion);
    this.sendBtn.textContent = isDiscussion ? (this.t.sendDiscussion || 'Send to discussion') : (this.t.sendAnswer || 'Send answer');
  }

  private async postDiscussion(content: string) {
    // Discussion is a plain, non-linked, non-dispatching comment; it fans out over SSE and renders
    // through the same discussion path on arrival. We optimistically render the local copy.
    this.appendDiscussion({author_name: this.t.you || 'You'}, content);
    try {
      await POST(this.answerUrl, {data: {RepoID: this.repoId, IssueID: this.issueId, Content: content}, headers: csrfHeaders()});
    } catch {
      // best-effort; the durable thread is the source of truth
    }
  }

  // ---- small helpers ------------------------------------------------------------------------

  private setPanelDisabled(disabled: boolean) {
    for (const b of this.active.querySelectorAll('button')) b.disabled = disabled;
    this.active.setAttribute('aria-disabled', String(disabled));
  }

  private showActive() {
    this.active.hidden = false;
    this.active.classList.remove('tw-hidden');
  }

  private hideEmpty() {
    const empty = this.transcript.querySelector<HTMLElement>('[data-role="empty"]');
    if (empty) empty.remove();
  }

  private scrollIfPinned() {
    const pinnedToBottom = this.transcript.scrollHeight - this.transcript.scrollTop - this.transcript.clientHeight < 80;
    if (pinnedToBottom) this.transcript.scrollTop = this.transcript.scrollHeight;
  }

  private announce(text: string) {
    if (!text) return;
    this.working.querySelector('.interview-working-text')?.setAttribute('data-announce', text);
  }
}

// initInterviewChat constructs and starts the chat controller for a root element. It returns the
// controller so unit tests can drive renderActiveQuestion directly; the global-init registration
// below adapts it to the void-returning GlobalInitFunc signature.
export function initInterviewChat(root: HTMLElement): InterviewChat {
  const chat = new InterviewChat(root);
  chat.init();
  return chat;
}

registerGlobalInitFunc('initInterviewChat', (root: HTMLElement): void => {
  initInterviewChat(root);
});
